import React, { useEffect, useState, useRef } from "react";
import { Send, Paperclip, X } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

export default function TicketThread({ ticketId, onReplySuccess }) {
  const [thread,  setThread]  = useState([]);
  const [message, setMessage] = useState("");
  const [file,    setFile]    = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!ticketId) { setThread([]); return; }
    const token = localStorage.getItem("token");
    setLoading(true);
    fetch(`${API_BASE}/api/tickets/thread/${ticketId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => setThread(Array.isArray(d) ? d : []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [ticketId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!ticketId || !message.trim()) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("message", message);
      if (file) fd.append("attachment", file);

      const res = await fetch(`${API_BASE}/api/tickets/reply/${ticketId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: fd,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const entry = await res.json();
      setThread(t => [...t, entry]);
      setMessage("");
      setFile(null);
      onReplySuccess?.(entry);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!ticketId) {
    return (
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Thread</span>
        </div>
        <div className="p-8 text-center">
          <p className="text-white/20 text-sm italic">Select a ticket to view the conversation.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">
          Thread · #{ticketId}
        </span>
      </div>

      {/* Messages */}
      <div className="p-4 space-y-3 max-h-72 overflow-y-auto">
        {loading && (
          <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-16 rounded-lg bg-white/5 animate-pulse" />)}</div>
        )}
        {error && <p className="text-red-400 text-sm">⚠ {error}</p>}
        {!loading && !error && thread.length === 0 && (
          <p className="text-white/20 text-sm italic text-center py-4">No messages yet.</p>
        )}
        {!loading && thread.map((msg, i) => {
          const isOwn = msg.sender_type === "user" || msg.is_own;
          return (
            <div key={i} className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-xl px-4 py-3 ${
                isOwn
                  ? "bg-[#00ff90]/10 border border-[#00ff90]/20"
                  : "bg-white/5 border border-white/8"
              }`}>
                <p className="text-xs text-white/30 mb-1">
                  {msg.sender || "Support"} · {(msg.timestamp || msg.created_at || "").replace("T", " ").slice(0, 16)}
                </p>
                <p className="text-sm text-white/80 whitespace-pre-wrap">{msg.message}</p>
                {msg.attachment_url && (
                  <a href={msg.attachment_url} target="_blank" rel="noreferrer"
                    className="text-xs text-[#00ff90]/60 hover:text-[#00ff90] mt-1 block transition-colors">
                    📎 View attachment
                  </a>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Reply input */}
      <div className="px-4 pb-4">
        <form onSubmit={handleReply} className="flex items-end gap-2">
          <div className="flex-1">
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleReply(e); } }}
              placeholder="Type a reply… (Enter to send, Shift+Enter for new line)"
              rows={2}
              className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors resize-none placeholder-white/20"
              disabled={busy}
            />
            {file && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-white/30 truncate">📎 {file.name}</span>
                <button type="button" onClick={() => setFile(null)} className="text-white/20 hover:text-white transition-colors"><X size={11} /></button>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1.5 shrink-0">
            <label className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-white cursor-pointer transition-colors">
              <Paperclip size={13} />
              <input type="file" className="hidden" onChange={e => setFile(e.target.files[0])} />
            </label>
            <button type="submit" disabled={busy || !message.trim()}
              className="w-8 h-8 rounded-lg bg-[#00ff90]/15 border border-[#00ff90]/30 text-[#00ff90] hover:bg-[#00ff90]/25 disabled:opacity-30 transition-colors flex items-center justify-center">
              <Send size={13} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}